import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Plus, Car, Check, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { listEvents } from '../lib/events';
import { toggleRsvp } from '../lib/rsvp';
import { useAuth } from '../lib/auth';
import BroadcastModal from '../components/BroadcastModal';

const CATS = ['All', 'Meet', 'Cars & Coffee', 'Track Day', 'Show'];

export default function Events() {
  const { user } = useAuth();
  const [cat, setCat] = React.useState('All');
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [broadcastFor, setBroadcastFor] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await listEvents({ limit: 100 });
      setEvents(data);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load, user]);

  const list = cat === 'All' ? events : events.filter((e) => e.category === cat);

  const rsvp = async (e) => {
    if (!user) { toast.error('Sign in to RSVP'); return; }
    try {
      setBusy(e.id);
      const { going, attendees } = await toggleRsvp(e.id);
      setEvents((prev) => prev.map((x) => x.id === e.id ? { ...x, going, attendees } : x));
      toast.success(going ? `You're going to ${e.title}` : `RSVP cancelled`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'RSVP failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14" data-testid="page-events">
      <div className="grid lg:grid-cols-12 items-end gap-6 mb-10">
        <div className="lg:col-span-8">
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Events</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">Meets, shows, track days.</h1>
          <p className="mt-3 text-zinc-400 text-sm">RSVP with one tap. Discover events near you.</p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link to="/host-event" className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-4 py-2.5 transition-colors" data-testid="host-event-btn">
            <Plus className="w-4 h-4"/> Host an event
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="event-categories">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`event-cat-${c.replace(/\s|&/g,'').toLowerCase()}`}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-2 transition-colors ${
              cat === c ? 'bg-[#ff2a2a] text-white' : 'hairline text-zinc-400 hover:text-white'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 font-mono text-xs uppercase tracking-widest" data-testid="events-loading">Loading events…</div>
      ) : list.length === 0 ? (
        <div className="am-card p-16 text-center" data-testid="events-empty">
          <Calendar className="w-10 h-10 mx-auto text-zinc-700" />
          <div className="mt-4 font-display text-2xl uppercase tracking-tight text-white">
            {events.length === 0 ? 'No events yet' : 'No events in this category'}
          </div>
          <p className="mt-2 text-zinc-400 text-sm max-w-md mx-auto">
            {events.length === 0
              ? 'Meets, cars & coffee, track days, shows — kick off the calendar by hosting the first one.'
              : 'Pick another category or host your own event.'}
          </p>
          {events.length === 0 && (
            <Link to="/host-event" className="mt-6 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3 transition-colors">Host an event</Link>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
          {list.map((e) => (
            <article key={e.id} className="am-card overflow-hidden group flex flex-col" data-testid={`event-${e.id}`}>
              <div className="relative aspect-[16/10] overflow-hidden bg-[#131313]">
                {e.img ? (
                  <img src={e.img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-700"><Car className="w-10 h-10"/></div>
                )}
                <div className="absolute top-3 left-3 am-pill bg-black/60 backdrop-blur-md">{e.category}</div>
                <div className="absolute bottom-3 left-3 bg-[#ff2a2a] text-white px-3 py-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest">{new Date(e.date).toLocaleDateString('en-CA',{month:'short'})}</div>
                  <div className="font-display text-2xl leading-none">{new Date(e.date).getDate()}</div>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-white font-semibold">{e.title}</h3>
                <div className="mt-3 space-y-1.5 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                  <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3"/>{e.date}{e.time ? ` · ${e.time}` : ''}</div>
                  {(e.venue || e.city) && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{[e.venue, e.city].filter(Boolean).join(', ')}</div>}
                  {e.host_name && (
                    <div className="flex items-center gap-1.5"><Users className="w-3 h-3"/>
                      Hosted by <Link to={`/users/${e.host_user_id}`} className="text-white hover:text-[#ff2a2a] transition-colors">{e.host_name}</Link>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => rsvp(e)}
                  disabled={busy === e.id}
                  className={`mt-4 w-full text-sm font-semibold py-2.5 transition-colors inline-flex items-center justify-center gap-2 ${
                    e.going ? 'bg-emerald-500 hover:bg-emerald-600 text-black' : 'hairline hover:border-[#ff2a2a] hover:text-white text-zinc-200'
                  } disabled:opacity-60`}
                  data-testid={`rsvp-${e.id}`}
                >
                  {e.going ? (<><Check className="w-3.5 h-3.5" /> Going · {e.attendees}</>) : (<>RSVP · {e.attendees ?? 0} going</>)}
                </button>
                {user && e.host_user_id === user.user_id && (
                  <button
                    onClick={() => setBroadcastFor(e)}
                    className="mt-2 w-full hairline hover:border-[#ff2a2a] text-zinc-300 hover:text-white text-xs font-mono uppercase tracking-widest py-2 inline-flex items-center justify-center gap-1.5"
                    data-testid={`broadcast-${e.id}`}
                  >
                    <Megaphone className="w-3 h-3" /> Message attendees
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <BroadcastModal open={!!broadcastFor} onClose={() => setBroadcastFor(null)} event={broadcastFor} />
    </div>
  );
}
